import { useState } from "react";
import { FiCopy, FiCheck, FiExternalLink } from "react-icons/fi";

import {
  BRAND_YELLOW,
  ENDPOINT,
  SKILLS_REPO,
  SETUP_STEPS,
  PROMPTS,
  SKILLS,
  ROUTES,
} from "@/pages/recruiter/mcp/data";

const DOT_GRID_STYLE = {
  backgroundImage: "radial-gradient(circle, rgba(0,0,0,0.08) 1px, transparent 1px)",
  backgroundSize: "24px 24px",
};

export default function Mcp() {
  return (
    <div className="min-h-screen bg-white" style={DOT_GRID_STYLE}>
      <div className="max-w-5xl mx-auto px-6 py-16">
        <Hero />
        <Setup />
        <Routes />
        <Prompts />
        <Skills />
        <Footer />
      </div>
    </div>
  );
}

function Hero() {
  return (
    <section className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-24">
      <div>
        <h1 className="text-5xl sm:text-6xl font-extrabold text-black tracking-tight mb-4 leading-[1.05]">
          Jobego MCP
        </h1>
        <p className="text-lg text-black/60 mb-8 leading-relaxed">
          Connect your Jobego account to Claude. Search jobs, review candidates, send messages — without leaving your AI.
        </p>
        <CopyField label="MCP Endpoint" value={ENDPOINT} />
        <div className="mt-4 flex items-center gap-2 text-xs text-black/50">
          <span className="relative flex w-2 h-2">
            <span className="absolute inline-flex w-full h-full rounded-full bg-green-500 opacity-60 animate-ping" />
            <span className="relative inline-flex w-2 h-2 rounded-full bg-green-500" />
          </span>
          Operational
        </div>
      </div>
      <div className="flex justify-center lg:justify-end">
        <SettingsIllustration />
      </div>
    </section>
  );
}

function SettingsIllustration() {
  return (
    <div className="w-full max-w-md flex items-center justify-center gap-3">
      <div
        className="flex-1 aspect-square rounded-2xl border-2 border-black shadow-[6px_6px_0_0_rgba(0,0,0,1)] flex flex-col items-center justify-center gap-2"
        style={{ backgroundColor: BRAND_YELLOW }}>
        <div className="w-10 h-10 rounded-full bg-black flex items-center justify-center text-white font-extrabold text-xs">
          AJ
        </div>
        <div className="font-extrabold text-black">Jobego</div>
      </div>
      <div className="flex-shrink-0 text-3xl font-extrabold text-black">↔</div>
      <div className="flex-1 aspect-square rounded-2xl border-2 border-black shadow-[6px_6px_0_0_rgba(0,0,0,1)] bg-black flex flex-col items-center justify-center gap-2">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center text-black font-extrabold"
          style={{ backgroundColor: BRAND_YELLOW }}>
          C
        </div>
        <div className="font-extrabold text-white">Claude</div>
      </div>
    </div>
  );
}

function Setup() {
  return (
    <section className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mt-16">
      <div>
        <h2 className="text-2xl font-extrabold text-black mb-6">How to setup</h2>
        <ol className="relative">
          <span className="absolute left-[11px] top-4 bottom-4 w-px bg-black/10" />
          {SETUP_STEPS.map((step, i) => (
            <li key={i} className="relative flex gap-4 pb-5 last:pb-0">
              <span
                className="relative z-10 flex-shrink-0 w-6 h-6 rounded-full text-black text-xs font-extrabold flex items-center justify-center"
                style={{ backgroundColor: BRAND_YELLOW }}>
                {i + 1}
              </span>
              <span className="text-sm text-black leading-relaxed pt-0.5">{step}</span>
            </li>
          ))}
        </ol>
      </div>
      <div className="flex justify-center lg:justify-end">
        <ChatIllustration />
      </div>
    </section>
  );
}

function ChatIllustration() {
  return (
    <div className="w-full max-w-md flex flex-col gap-3">
      <div className="self-end max-w-[80%] border-2 border-black rounded-2xl rounded-br-sm px-4 py-3 shadow-[4px_4px_0_0_rgba(0,0,0,1)]">
        <div className="text-[10px] font-extrabold uppercase tracking-widest text-black/50 mb-1">You</div>
        <div className="text-sm font-bold text-black">List top candidates</div>
      </div>
      <div
        className="self-start max-w-[80%] border-2 border-black rounded-2xl rounded-bl-sm px-4 py-3 shadow-[4px_4px_0_0_rgba(0,0,0,1)]"
        style={{ backgroundColor: BRAND_YELLOW }}>
        <div className="text-[10px] font-extrabold uppercase tracking-widest text-black/60 mb-1">Claude</div>
        <code className="text-sm font-mono font-extrabold text-black">search_applications()</code>
      </div>
    </div>
  );
}

function Routes() {
  return (
    <section className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-12 items-start mt-24">
      <div>
        <h2 className="text-2xl font-extrabold text-black mb-6">Available routes</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
          {ROUTES.map((group) => (
            <div key={group.group}>
              <div className="flex items-baseline gap-3 mb-3">
                <h3
                  className="text-[11px] font-extrabold uppercase tracking-widest"
                  style={{ color: BRAND_YELLOW }}>
                  {group.group}
                </h3>
                <span className="flex-1 h-px bg-black/10" />
              </div>
              <ul className="space-y-2">
                {group.tools.map((tool) => (
                  <li key={tool.name} className="flex flex-col text-sm">
                    <code className="font-mono font-bold text-black text-[13px]">{tool.name}</code>
                    <span className="text-black/60 text-xs">{tool.description}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
      <div className="hidden lg:block">
        <BracesAccent />
      </div>
    </section>
  );
}

function BracesAccent() {
  return (
    <div
      className="sticky top-16 select-none font-mono font-extrabold leading-none"
      style={{ color: BRAND_YELLOW, fontSize: "14rem", textShadow: "4px 4px 0 rgba(0,0,0,1)" }}
      aria-hidden="true">
      {"{}"}
    </div>
  );
}

function Prompts() {
  return (
    <section className="mt-24">
      <h2 className="text-2xl font-extrabold text-black mb-2">Prompts</h2>
      <p className="text-sm text-black/60 mb-8 max-w-2xl">
        Pre-built workflows available in Claude once connected. Select one from the prompts menu and fill in the parameters.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {PROMPTS.map((p) => (
          <div
            key={p.name}
            className="border-2 border-black rounded-2xl p-5 bg-white shadow-[4px_4px_0_0_rgba(0,0,0,1)] flex flex-col">
            <code className="font-mono font-extrabold text-black text-sm mb-2">{p.name}</code>
            <p className="text-xs text-black/60 leading-relaxed flex-1 mb-4">{p.description}</p>
            <div className="flex flex-wrap gap-1.5">
              {p.params.map((param) => (
                <span
                  key={param}
                  className="text-[10px] font-mono font-bold text-black border-2 border-black rounded px-1.5 py-0.5"
                  style={{ backgroundColor: BRAND_YELLOW }}>
                  {param}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function Skills() {
  return (
    <section className="mt-24">
      <div className="flex items-end justify-between gap-4 mb-2">
        <h2 className="text-2xl font-extrabold text-black">Skills</h2>
        <a
          href={SKILLS_REPO}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-xs font-bold text-black hover:opacity-70">
          View on GitHub
          <FiExternalLink className="w-3.5 h-3.5" />
        </a>
      </div>
      <p className="text-sm text-black/60 mb-8 max-w-2xl">
        Install these skills on Claude Desktop or Claude Code to coach the model on Jobego-specific workflows. Open-source, edit to fit your team.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {SKILLS.map((s) => (
          <div
            key={s.name}
            className="border-2 border-black rounded-2xl p-5 bg-white shadow-[4px_4px_0_0_rgba(0,0,0,1)] flex flex-col">
            <div className="flex items-baseline gap-2 mb-2">
              <code className="font-mono font-extrabold text-black text-sm">{s.name}</code>
              {s.status === "coming_soon" && (
                <span className="text-[9px] font-extrabold uppercase tracking-widest text-black/50">
                  Coming soon
                </span>
              )}
            </div>
            <p className="text-xs text-black/60 leading-relaxed flex-1 mb-4">{s.description}</p>
            {s.status === "available" ? (
              <a
                href={`${SKILLS_REPO}/${s.name}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-1.5 py-2 rounded-md text-black text-xs font-extrabold border-2 border-black"
                style={{ backgroundColor: BRAND_YELLOW }}>
                Install
                <FiExternalLink className="w-3.5 h-3.5" />
              </a>
            ) : (
              <span className="inline-flex items-center justify-center py-2 rounded-md text-black/40 text-xs font-extrabold border-2 border-black/20 bg-black/5">
                Not yet available
              </span>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function Footer() {
  return (
    <p className="mt-20 pt-6 border-t border-black/10 text-xs text-black/50">
      Every call is scoped to your account and requires an organisation you belong to. Authentication via OAuth 2.1.
    </p>
  );
}

function CopyField({ label, value }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div>
      <label className="block text-xs font-extrabold uppercase tracking-widest text-black/50 mb-2">
        {label}
      </label>
      <div className="flex items-center gap-2 border-2 border-black rounded-lg px-3 py-3 bg-white">
        <code className="flex-1 text-sm font-mono text-black truncate">{value}</code>
        <button
          onClick={handleCopy}
          className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-md hover:bg-black/5 text-black transition-colors cursor-pointer"
          aria-label="Copy">
          {copied ? (
            <FiCheck className="w-4 h-4" style={{ color: BRAND_YELLOW }} />
          ) : (
            <FiCopy className="w-4 h-4" />
          )}
        </button>
      </div>
    </div>
  );
}
